import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import promptWorkflowService from '../../services/promptWorkflowService';
import promptService from '../../services/promptService';
import {
	Box,
	Card,
	CardContent,
	Typography,
	Button,
	List,
	IconButton,
	Paper,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Divider,
	Alert,
	CircularProgress,
	Skeleton,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import RestoreIcon from '@mui/icons-material/Restore';
import CloseIcon from '@mui/icons-material/Close';
import PromptListItem from './PromptListItem';
import { StyledDrawer } from '../common/StyledDrawer';
import ConfirmationDialog from '../common/ConfirmationDialog';

/**
 * PromptWorkflows Component
 *
 * Manages prompt workflows which define which prompts run in what sequence for different events.
 * This provides a user-friendly interface for configuring prompt sequences while using the
 * backend orchestration service API. Initial implementation focuses on "deal_creation" event type.
 */
const PromptWorkflows = ({ showNotification }) => {
	const { tenantId } = useParams();
	const theme = useTheme();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [prompts, setPrompts] = useState([]);
	const [eventTypes] = useState([
		{ value: 'deal_creation', label: 'Deal Creation' },
		{
			value: 'placeholder_event',
			label: 'Other Event (Coming Soon)',
			disabled: true,
		},
	]);
	const [selectedEventType, setSelectedEventType] = useState('deal_creation');
	const [promptWorkflow, setPromptWorkflow] = useState(null);
	const [originalWorkflow, setOriginalWorkflow] = useState(null);
	const [availablePrompts, setAvailablePrompts] = useState([]);
	const [selectedPromptId, setSelectedPromptId] = useState('');
	const [lastAddedIndex, setLastAddedIndex] = useState(null);

	// New state for drawer and content management
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [selectedSequencePrompt, setSelectedSequencePrompt] = useState(null);
	const [promptContents, setPromptContents] = useState({}); // Cache: { prompt_id: content }
	const [loadingContent, setLoadingContent] = useState(false);
	const [contentError, setContentError] = useState(null);

	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// Fetch prompts from the API
	useEffect(() => {
		const fetchPrompts = async () => {
			try {
				const response = await promptService.getPrompts(tenantId, {
					includeTenantDefault: true,
				});
				setPrompts(response || []);
			} catch (error) {
				console.error('Error loading prompts for orchestration:', error);
				setError('Failed to load prompt templates. Please try again.');
			}
		};

		fetchPrompts();
	}, [tenantId]);

	// Fetch the prompt workflow for the selected event type
	useEffect(() => {
		const fetchPromptWorkflow = async () => {
			// Ensure we have the required parameters
			if (!selectedEventType) {
				console.warn('No event type selected, cannot fetch prompt workflow');
				return;
			}

			if (!tenantId) {
				console.warn('No tenant ID available, cannot fetch prompt workflow');
				setError('Tenant ID is required to load prompt workflow');
				return;
			}

			setLoading(true);
			setError(null);

			try {
				const response = await promptWorkflowService.getPromptWorkflow(
					tenantId,
					selectedEventType
				);

				const config = {
					id: response.id, // Will be null if using system default
					event_type: response.event_type,
					tenant_id: response.tenant_id,
					prompt_sequence: response.prompt_sequence || [],
				};

				setPromptWorkflow(config);
				// Store the original config for change detection
				setOriginalWorkflow(JSON.stringify(config));
			} catch (err) {
				console.error('Failed to fetch workflow configuration:', err);
				setError('Failed to load workflow configuration. Please try again.');
			} finally {
				setLoading(false);
			}
		};

		fetchPromptWorkflow();
	}, [tenantId, selectedEventType]);

	// Filter prompts for the available prompts dropdown
	useEffect(() => {
		// Check if promptWorkflow exists
		if (!promptWorkflow) {
			// If no workflow yet, all prompts are available
			setAvailablePrompts([...prompts]);
			return;
		}

		// Get IDs of prompts already in sequence
		const usedPromptIds = new Set(
			promptWorkflow.prompt_sequence.map((item) => item.prompt_id)
		);

		// Filter available prompts to those not already in the sequence
		const filtered = prompts.filter((prompt) => !usedPromptIds.has(prompt.id));
		setAvailablePrompts(filtered);

		// Reset selected prompt if needed
		if (
			filtered.length > 0 &&
			!filtered.some((p) => p.id === selectedPromptId)
		) {
			setSelectedPromptId('');
		}
	}, [
		prompts,
		promptWorkflow?.prompt_sequence,
		selectedPromptId,
		promptWorkflow,
	]);

	const handleEventTypeChange = (event) => {
		setSelectedEventType(event.target.value);
	};

	const handlePromptSelectionChange = (e) => {
		const promptId = e.target.value;

		// Only proceed if a valid prompt is selected
		if (promptId) {
			// Add the selected prompt to the sequence immediately
			const updatedWorkflow = {
				...promptWorkflow,
				prompt_sequence: [
					...promptWorkflow.prompt_sequence,
					{ prompt_id: promptId },
				],
			};

			setPromptWorkflow(updatedWorkflow);

			// Set the index of the last added item for highlight effect
			setLastAddedIndex(promptWorkflow.prompt_sequence.length);

			// Clear the selection after adding
			setSelectedPromptId('');
		} else {
			// If nothing or empty string selected, just update the state
			setSelectedPromptId(promptId);
		}
	};

	// Handle drag and drop reordering
	const handleDragEnd = (result) => {
		// Drop outside the list or no movement
		if (
			!result.destination ||
			result.destination.index === result.source.index
		) {
			return;
		}

		// Ensure promptWorkflow exists
		if (!promptWorkflow || !promptWorkflow.prompt_sequence) {
			return;
		}

		const newPromptSequence = [...promptWorkflow.prompt_sequence];
		const [movedItem] = newPromptSequence.splice(result.source.index, 1);
		newPromptSequence.splice(result.destination.index, 0, movedItem);

		// Update order values
		newPromptSequence.forEach((item, idx) => {
			item.order = idx + 1;
		});

		setPromptWorkflow({
			...promptWorkflow,
			prompt_sequence: newPromptSequence,
		});
	};

	const handleRemovePrompt = (index) => {
		// Check if promptWorkflow exists
		if (!promptWorkflow || !promptWorkflow.prompt_sequence) {
			return;
		}

		if (index < 0 || index >= promptWorkflow.prompt_sequence.length) return;

		// Create a deep copy and remove the item
		const newSequence = promptWorkflow.prompt_sequence
			.filter((_, idx) => idx !== index)
			.map((item, idx) => ({ ...item, order: idx + 1 })); // Reorder

		setPromptWorkflow({
			...promptWorkflow,
			prompt_sequence: newSequence,
		});
	};

	const handleUseSystemDefault = () => {
		setConfirmDialogOpen(true);
	};

	const confirmUseSystemDefault = async () => {
		setConfirmDialogOpen(false);
		setLoading(true);
		setError(null);

		try {
			setLoading(true);
			// Delete the workflow config to revert to system default
			await promptWorkflowService.deletePromptWorkflow(
				tenantId,
				selectedEventType
			);

			// Fetch the system default workflow after deletion
			const response = await promptWorkflowService.getPromptWorkflow(
				tenantId,
				selectedEventType
			);

			// Reset local state with system defaults
			const workflow = {
				...response,
				prompt_sequence: response.prompt_sequence || [],
			};
			setPromptWorkflow(workflow);

			// Update original workflow after reverting to system default
			setOriginalWorkflow(JSON.stringify(workflow));
			setConfirmDialogOpen(false);
			showNotification(
				'System default workflow restored successfully',
				'success'
			);
		} catch (err) {
			console.error('Error resetting to system default:', err);
			setError('Failed to reset to system default. Please try again.');
			showNotification(
				'Failed to restore system default workflow',
				'error',
				6000
			);
		} finally {
			setLoading(false);
		}
	};

	const handleCloseConfirmDialog = () => {
		setConfirmDialogOpen(false);
	};

	const handleSaveWorkflow = async () => {
		setSaving(true);
		setError(null);

		try {
			const savedWorkflow = await promptWorkflowService.savePromptWorkflow(
				tenantId,
				promptWorkflow
			);

			// Update local state with saved workflow
			const workflow = {
				...savedWorkflow,
				prompt_sequence: savedWorkflow.prompt_sequence || [],
			};
			setPromptWorkflow(workflow);

			// Update original workflow after save to reset change detection
			setOriginalWorkflow(JSON.stringify(workflow));

			// Show success notification
			showNotification('Workflow saved successfully', 'success');
		} catch (err) {
			console.error('Failed to save prompt workflow:', err);
			setError('Failed to save workflow. Please try again.');
			showNotification('Failed to save workflow', 'error', 6000);
		} finally {
			setSaving(false);
		}
	};

	// Helper function to find prompt name by ID
	const getPromptName = (promptId) => {
		const prompt = prompts.find((p) => p.id === promptId);
		return prompt ? prompt.name : 'Unknown Prompt';
	};

	// Function to handle selecting a prompt from sequence
	const handleSelectSequencePrompt = async (prompt) => {
		setSelectedSequencePrompt(prompt);
		setDrawerOpen(true);

		// Only fetch if not already cached
		if (!promptContents[prompt.prompt_id]) {
			await fetchPromptContent(prompt.prompt_id);
		}
	};

	// Function to fetch and cache prompt content
	const fetchPromptContent = async (promptId) => {
		if (promptContents[promptId]) return; // Already cached

		setLoadingContent(true);
		setContentError(null);

		try {
			const promptData = await promptService.getPrompt(tenantId, promptId);

			// Only cache the prompt data if it has content
			if (promptData && promptData.content) {
				setPromptContents((prev) => ({
					...prev,
					[promptId]: promptData,
				}));
			} else {
				// Handle the case where content is missing
				setContentError('No content available for this prompt');
				console.warn(
					`Prompt ${promptId} has no content attribute:`,
					promptData
				);
			}
		} catch (err) {
			console.error('Failed to load prompt content:', err);
			setContentError(
				`Failed to load prompt content: ${err.message || 'Unknown error'}`
			);
		} finally {
			setLoadingContent(false);
		}
	};

	// Function to close the drawer
	const handleCloseDrawer = () => {
		setDrawerOpen(false);
		setSelectedSequencePrompt(null);
	};

	// Effects for highlighting newly added prompt
	useEffect(() => {
		// If we have a lastAddedIndex, clear it after a delay
		if (lastAddedIndex !== null) {
			const timer = setTimeout(() => {
				setLastAddedIndex(null);
			}, 2000); // 2 second highlight
			return () => clearTimeout(timer);
		}
	}, [lastAddedIndex]);

	// Determine which workflow to display - always show the promptWorkflow to see immediate changes
	// This ensures we always see changes like deletions immediately, even when working with system defaults
	const displayWorkflow = promptWorkflow;

	// Determine if there are unsaved changes by comparing current workflow with original
	const hasChanges = useMemo(() => {
		if (!promptWorkflow || !originalWorkflow) return false;

		// Compare the current workflow with the original one
		const currentWorkflowString = JSON.stringify(promptWorkflow);
		return currentWorkflowString !== originalWorkflow;
	}, [promptWorkflow, originalWorkflow]);

	return (
		<Box sx={{ mt: 2 }}>
			{error && (
				<Alert severity='error' sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Card variant='outlined' sx={{ mb: 3 }}>
				<CardContent>
					<FormControl fullWidth margin='normal'>
						<InputLabel id='event-type-select-label'>Event Type</InputLabel>
						<Select
							labelId='event-type-label'
							value={selectedEventType}
							label='Event Type'
							onChange={handleEventTypeChange}
							disabled={saving || loading}
						>
							{eventTypes.map((type) => (
								<MenuItem
									key={type.value}
									value={type.value}
									disabled={type.disabled}
								>
									{type.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
							<CircularProgress />
						</Box>
					) : (
						<>
							{promptWorkflow?.tenant_id === null && (
								<Alert severity='info' sx={{ mb: 2 }}>
									You are using the system default workflow for this event type.
								</Alert>
							)}

							<Box sx={{ mb: 2 }}>
								<Typography variant='subtitle2' gutterBottom>
									Prompt Sequence
								</Typography>
								<Paper variant='outlined'>
									{displayWorkflow?.prompt_sequence?.length > 0 ? (
										<DragDropContext onDragEnd={handleDragEnd}>
											<Droppable
												droppableId='prompt-sequence'
												isDropDisabled={false}
												renderClone={(provided, snapshot, rubric) => {
													const item =
														displayWorkflow.prompt_sequence[
															rubric.source.index
														];
													return (
														<PromptListItem
															item={item}
															index={rubric.source.index}
															isClone={true}
															provided={provided}
															getPromptName={getPromptName}
															handleRemovePrompt={handleRemovePrompt}
															handleSelectSequencePrompt={
																handleSelectSequencePrompt
															}
														/>
													);
												}}
											>
												{(provided) => (
													<List
														dense
														ref={provided.innerRef}
														{...provided.droppableProps}
													>
														{displayWorkflow.prompt_sequence.map(
															(item, index) => (
																<Draggable
																	key={`${item.prompt_id}-${index}`}
																	draggableId={`${item.prompt_id}-${index}`}
																	index={index}
																	isDragDisabled={false}
																>
																	{(provided) => (
																		<PromptListItem
																			item={item}
																			index={index}
																			isClone={false}
																			provided={provided}
																			getPromptName={getPromptName}
																			handleRemovePrompt={handleRemovePrompt}
																			handleSelectSequencePrompt={
																				handleSelectSequencePrompt
																			}
																			isHighlighted={lastAddedIndex === index}
																		/>
																	)}
																</Draggable>
															)
														)}
														{provided.placeholder}
													</List>
												)}
											</Droppable>
										</DragDropContext>
									) : (
										<Box sx={{ p: 2, textAlign: 'center' }}>
											<Typography variant='body2' color='text.secondary'>
												No prompts configured for this event type.
											</Typography>
										</Box>
									)}
								</Paper>
							</Box>

							<Divider sx={{ my: 2 }} />

							<Box sx={{ mb: 2 }}>
								<Typography variant='subtitle2' gutterBottom>
									Add Prompt to Sequence
								</Typography>

								<Box sx={{ mb: 1 }}>
									<FormControl fullWidth margin='dense'>
										<Select
											labelId='available-prompts-label'
											value={selectedPromptId}
											displayEmpty
											onChange={handlePromptSelectionChange}
											disabled={saving || availablePrompts.length === 0}
										>
											<MenuItem value=''>Select a prompt to add</MenuItem>
											{availablePrompts.map((prompt) => (
												<MenuItem key={prompt.id} value={prompt.id}>
													{prompt.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>
							</Box>

							<Box
								sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}
							>
								{promptWorkflow && promptWorkflow.tenant_id !== null && (
									<Button
										variant='outlined'
										startIcon={<RestoreIcon />}
										onClick={handleUseSystemDefault}
										disabled={saving}
									>
										Use System Default
									</Button>
								)}

								<Button
									variant='contained'
									color='primary'
									onClick={handleSaveWorkflow}
									disabled={saving || !hasChanges}
									sx={{
										opacity: saving || !hasChanges ? 0.6 : 1,
										'&.Mui-disabled': {
											backgroundColor: 'rgba(25, 118, 210, 0.5)',
											color: 'white',
											cursor: 'not-allowed',
										},
									}}
								>
									{saving ? (
										<CircularProgress size={24} color='inherit' />
									) : (
										'Save Workflow'
									)}
								</Button>
							</Box>
						</>
					)}
				</CardContent>
			</Card>

			{/* Confirmation Dialog for Use System Default */}
			<ConfirmationDialog
				open={confirmDialogOpen}
				handleCancel={handleCloseConfirmDialog}
				title='Use System Default Workflow?'
				message='This will remove your custom prompt sequence workflow and use the system default. Are you sure you want to continue?'
				handleConfirm={confirmUseSystemDefault}
				confirmActionButtonLabel='Confirm'
			/>

			{/* Drawer for displaying prompt content */}
			<StyledDrawer
				anchor={isMobile ? 'bottom' : 'right'}
				open={drawerOpen}
				onClose={handleCloseDrawer}
				isMobile={isMobile}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							borderBottom: `1px solid ${theme.palette.divider}`,
							p: 2,
						}}
					>
						<Typography variant='h6'>
							{selectedSequencePrompt &&
								getPromptName(selectedSequencePrompt.prompt_id)}
						</Typography>
						<IconButton onClick={handleCloseDrawer}>
							<CloseIcon />
						</IconButton>
					</Box>

					<Divider sx={{ mb: 2 }} />

					{selectedSequencePrompt && (
						<Box sx={{ flex: 1, overflow: 'auto' }}>
							{loadingContent ? (
								<Box sx={{ p: 2 }}>
									<Skeleton variant='text' height={30} />
									<Skeleton variant='text' height={30} />
									<Skeleton variant='text' height={30} />
									<Skeleton variant='text' height={30} />
									<Skeleton variant='text' height={30} />
								</Box>
							) : contentError ? (
								<Alert severity='error'>{contentError}</Alert>
							) : promptContents[selectedSequencePrompt.prompt_id] ? (
								<Box sx={{ p: 2 }}>
									{/* Display prompt content */}
									<Typography variant='subtitle2' gutterBottom>
										Content:
									</Typography>
									<Paper
										variant='outlined'
										sx={{
											p: 2,
											mt: 1,
											whiteSpace: 'pre-wrap',
											fontFamily: 'monospace',
											fontSize: '0.9rem',
											maxHeight: '70vh',
											overflow: 'auto',
										}}
									>
										{promptContents[selectedSequencePrompt.prompt_id].content ||
											'No content available'}
									</Paper>
								</Box>
							) : (
								<Typography>Select a prompt to view its content</Typography>
							)}
						</Box>
					)}
				</Box>
			</StyledDrawer>
		</Box>
	);
};

export default PromptWorkflows;
